import {
  isEnrichedShift,
  type RouteAssignment,
  type RoutePlanCaregiver,
  type EnrichedExistingShift,
  type ExistingShiftStep,
} from '@/api/route-plans';
import { getCareerColorHex, tintWithWhite } from '@/utils/careers';

// Self-contained event model for the route-plan board calendar. Intentionally separate from the
// shifts Calendar v2 (calendar-events.ts): the two calendars serve different flows, use cases and
// backend models, so neither should perturb the other.
export interface RoutePlanCalendarEvent {
  start: string;
  end: string;
  title: string;
  content: string;
  class: string;
  schedule?: number;
  compact: boolean;
  colorHex: string;
  colorTint: string;
  // vue-cal per-event flags: only placed-visit blocks may be dragged.
  draggable: boolean;
  resizable: boolean;
  // Present only on placed-visit blocks; identifies the assignment on the board's event click.
  assignmentId?: number;
  // Patient address, shown first in the visit block's hover tooltip.
  address?: string;
  // Present only on lunch-break blocks; the caregiver whose lunch it is (owner never changes
  // on drag — only the time does).
  breakCaregiverId?: number;
  // Present only on existing-shift blocks; identifies the real Shift for board moves.
  existingShiftId?: number;
  // Existing shift with a manual move proposed (shown with the "modificado" badge).
  moved?: boolean;
  // The shift's original time range, for the tooltip when moved.
  originalTime?: string;
  // The shift's original caregiver, for the tooltip when the move reassigned it.
  originalCaregiverName?: string;
  // VROOM couldn't fold this shift into the route; it is honored at its committed time.
  unrouted?: boolean;
  // The solve never planned around this shift (created after optimizing, or no solve).
  unplanned?: boolean;
}

// Fraction of the career color kept in the block background — same tonal look as the
// shifts calendar (calendar-events.ts EVENT_TINT_RATIO).
const EVENT_TINT_RATIO = 0.15;
const EXISTING_SHIFT_COLOR = '#616161';
const EXISTING_SHIFT_TINT = '#eeeeee';
const BREAK_COLOR = '#e65100';
const BREAK_TINT = '#ffe0b2';
const HM_LENGTH = 5;
const MINUTES_PER_HOUR = 60;
// Below this a block renders as ONE line (name + time). Must stay in sync with the calendar's
// @container (max-height: 44px) rule that hides the second line: at 40px/hour, 44px ≈ 66 min,
// so anything under 70 min takes the one-line layout (a 60-min block is only 40px tall).
const COMPACT_THRESHOLD_MINUTES = 70;

function minutesOf(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);

  return (hours * MINUTES_PER_HOUR) + minutes;
}

// Columns are the plan's caregiver POOL (not derived from placed work), so idle / leftover
// caregivers still get a column to drop visits onto.
export function schedulesFromPool(
  caregivers: RoutePlanCaregiver[],
): Array<{ id: number; label: string }> {
  return caregivers
    .map((caregiver) => ({ id: caregiver.caregiverId, label: caregiver.caregiverFullName }))
    .sort((a, b) => a.label.localeCompare(b.label, 'es'));
}

function assignmentEvent(
  assignment: RouteAssignment,
  date: string,
  career: string,
): RoutePlanCalendarEvent {
  const colorHex = getCareerColorHex(career);
  const duration = minutesOf(assignment.humanDepartureTime) - minutesOf(assignment.humanArrivalTime);

  return {
    assignmentId: assignment.id,
    start: `${date} ${assignment.humanArrivalTime}`,
    end: `${date} ${assignment.humanDepartureTime}`,
    title: assignment.patientFullName,
    content: `${assignment.humanArrivalTime}–${assignment.humanDepartureTime}`,
    address: assignment.patientAddress ?? undefined,
    class: 'route-plan-event route-plan-assignment',
    draggable: true,
    resizable: false,
    schedule: assignment.caregiverId,
    compact: duration < COMPACT_THRESHOLD_MINUTES,
    colorHex,
    colorTint: tintWithWhite(colorHex, EVENT_TINT_RATIO),
  };
}

function existingShiftEvent(
  caregiverId: number,
  step: EnrichedExistingShift,
  date: string,
): RoutePlanCalendarEvent {
  const duration = minutesOf(step.humanEndTime) - minutesOf(step.humanStartTime);

  return {
    start: `${date} ${step.humanStartTime}`,
    end: `${date} ${step.humanEndTime}`,
    title: step.patientFullName ?? 'Turno existente',
    content: `${step.humanStartTime}–${step.humanEndTime}`,
    address: step.patientAddress ?? undefined,
    class: 'route-plan-event route-plan-existing-shift' +
      `${step.moved ? ' is-moved' : ''}${step.unplanned ? ' is-unplanned' : ''}`,
    schedule: caregiverId,
    existingShiftId: step.shiftId,
    moved: step.moved,
    unplanned: step.unplanned,
    originalTime: `${step.originalStartTime}–${step.originalEndTime}`,
    originalCaregiverName: step.originalCaregiverName ?? undefined,
    unrouted: step.dropped,
    compact: duration < COMPACT_THRESHOLD_MINUTES,
    // Safe shifts (future, unattended, chain-free) can be moved by hand like visits.
    draggable: step.movable,
    resizable: false,
    colorHex: EXISTING_SHIFT_COLOR,
    colorTint: EXISTING_SHIFT_TINT,
  };
}

// Company-tz wall-clock HH:MM from an offset-bearing ISO datetime, so breaks line up with the
// visits' human times regardless of the browser's zone.
function hmFromIso(iso: string): string {
  const match = iso.match(/T(\d{2}:\d{2})/);

  return match ? match[1]! : iso.slice(0, HM_LENGTH);
}

function breakEvent(
  caregiverId: number,
  brk: { startTime: string; endTime: string },
  date: string,
): RoutePlanCalendarEvent {
  const start = hmFromIso(brk.startTime);
  const end = hmFromIso(brk.endTime);

  return {
    start: `${date} ${start}`,
    end: `${date} ${end}`,
    title: 'Almuerzo',
    content: `${start}–${end}`,
    class: 'route-plan-event route-plan-break',
    schedule: caregiverId,
    breakCaregiverId: caregiverId,
    compact: minutesOf(end) - minutesOf(start) < COMPACT_THRESHOLD_MINUTES,
    // The lunch can be dragged to another time (within the company lunch window).
    draggable: true,
    resizable: false,
    colorHex: BREAK_COLOR,
    colorTint: BREAK_TINT,
  };
}

export interface RoutePlanEventsInput {
  existingShifts: Record<number, ExistingShiftStep[]>;
  caregiverBreaks: Record<number, { startTime: string; endTime: string }>;
  date: string;
  // The plan's career: every visit is colored with its career color, matching the shifts calendar.
  career: string;
}

// Events for the daily board: placed assignments (career color, like the shifts calendar),
// committed existing shifts (grey) and the lunch break (orange). Travel/wait detail lives in
// the timeline (Cronograma) tab, not on the calendar.
export function buildRoutePlanEvents(
  assignments: RouteAssignment[],
  { existingShifts, caregiverBreaks, date, career }: RoutePlanEventsInput,
): RoutePlanCalendarEvent[] {
  const enrichedShifts = Object.entries(existingShifts).map(
    ([caregiverId, steps]) => [Number(caregiverId), steps.filter(isEnrichedShift)] as const,
  );

  const assignmentEvents = assignments.map((assignment) => (
    assignmentEvent(assignment, date, career)
  ));

  const shiftEvents = enrichedShifts.flatMap(([caregiverId, steps]) => (
    steps.map((step) => existingShiftEvent(caregiverId, step, date))
  ));

  const breakEvents = Object.entries(caregiverBreaks).map(([caregiverId, brk]) => (
    breakEvent(Number(caregiverId), brk, date)
  ));

  return [...assignmentEvents, ...shiftEvents, ...breakEvents];
}
